import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Box, Flex, Text, useColorModeValue, VStack, Image, Badge } from '@chakra-ui/react';
import { motion } from 'framer-motion';

interface VideoTransitionSlideProps {
  videoSrc: string;
  title?: string;
  subtitle?: string;
  description?: string;
  onComplete?: () => void;
  autoPlay?: boolean;
  loop?: boolean;
  overlayColor?: string;
  accentColor?: string;
  titleColor?: string;
  subtitleColor?: string;
}

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

const VideoTransitionSlide = forwardRef<HTMLVideoElement, VideoTransitionSlideProps>((
  {
    videoSrc,
    title,
    subtitle,
    description,
    onComplete,
    autoPlay = true,
    loop = false,
    overlayColor = "methodist.navy",
    accentColor = "methodist.blue",
    titleColor = "white",
    subtitleColor = "methodist.green"
  }, ref
) => {
  const internalVideoRef = useRef<HTMLVideoElement>(null);
  const [progress, setProgress] = useState(0);
  
  // Forward the ref to the parent component
  useImperativeHandle(ref, () => internalVideoRef.current as HTMLVideoElement);

  useEffect(() => {
    const videoElement = internalVideoRef.current;
    if (!videoElement) return;

    // Set playback speed to 0.5 (half speed)
    videoElement.playbackRate = 0.5;

    const handleTimeUpdate = () => {
      if (videoElement.duration) {
        setProgress((videoElement.currentTime / videoElement.duration) * 100);
      }
    };

    const handleEnded = () => {
      if (onComplete && !loop) {
        onComplete();
      }
    };

    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    videoElement.addEventListener('ended', handleEnded);

    return () => {
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      videoElement.removeEventListener('ended', handleEnded);
    };
  }, [onComplete, loop]);

  return (
    <Box 
      position="relative" 
      width="100%" 
      height="100vh" 
      overflow="hidden"
      bg={overlayColor}
    >
      {/* Video Background */}
      <Box
        as="video"
        ref={internalVideoRef}
        position="absolute"
        top="0"
        left="0"
        width="100%"
        height="100%"
        objectFit="cover"
        autoPlay={autoPlay}
        loop={loop}
        muted
        playsInline
        src={videoSrc}
        zIndex={0}
      />

      {/* Subtle dark overlay for better text readability */}
      <Box
        position="absolute"
        top="0"
        left="0"
        width="100%"
        height="100%"
        bg="black"
        opacity={0.2}
        zIndex={1}
      />

      {/* Content */}
      <Flex
        position="relative"
        direction="column"
        align="flex-start"
        justify="space-between"
        height="100%"
        width="100%"
        zIndex={2}
        px={12}
        py={10}
      >
        {/* Title and subtitle - top right */}
        <MotionBox
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          alignSelf="flex-end"
          maxWidth="60%"
          position="absolute"
          top={8}
          right={8}
          zIndex={2}
        >
          <VStack align="flex-end" spacing={2}>
            {title && (
              <Box
                bg="methodist.blue"
                px={4}
                py={2}
                borderRadius="sm"
              >
                <Text
                  fontSize={{ base: "2xl", md: "3xl" }}
                  fontWeight="bold"
                  fontFamily="'Helvetica Neue', Helvetica, Arial, sans-serif"
                  color="white"
                  textAlign="right"
                  letterSpacing="tight"
                  lineHeight="1.2"
                >
                  {title}
                </Text>
              </Box>
            )}
            
            {subtitle && (
              <Box
                bg="white"
                px={4}
                py={2}
                borderRadius="sm"
              >
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  fontWeight="medium"
                  fontFamily="'Helvetica Neue', Helvetica, Arial, sans-serif"
                  color="methodist.blue"
                  textAlign="right"
                  maxW="600px"
                >
                  {subtitle}
                </Text>
              </Box>
            )}
          </VStack>
        </MotionBox>

        {/* Disclaimer Text - Bottom Right */}
        <MotionBox
          position="absolute"
          bottom={4}
          right={4}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <Box
            bg="rgba(0, 0, 0, 0.7)"
            px={3}
            py={1}
            borderRadius="sm"
          >
            <Text
              fontSize="xs"
              fontWeight="medium"
              color="white"
            >
              Artificially generated demonstration
            </Text>
          </Box>
        </MotionBox>
      </Flex>
    </Box>
  );
});

VideoTransitionSlide.displayName = 'VideoTransitionSlide';

export default VideoTransitionSlide;
